import React, { createContext, useContext, useMemo, useReducer } from "react";
import { childrenProps, legalCurriculumPdfParamType, legalCurriculumResultExportStatusType } from "../types";
import { LegalCurriculumExcelParams } from "../components/ExcelAction/LegalCurriculumExcel/LegalCurriculumMstExcel";
import { v4 as uuidv4 } from "uuid";

export type reportType = "LEGAL_CURRICULUM" | "LEGAL_CURRICULUM_EXCEL" | "SERVER_LEGAL_CURRICULUM_RESULT" | "SERVER_ACCOUNT_PORTAL_LEGAL_USER_LIST";
export type reportStatusType = "SUCCESS" | "ERROR" | "PROCESSING";
export type conditionInfoType = { key: string; label: string; value: string | number | string[] };

type pdfInfoBase = {
  id: string;
  // 出力帳票名
  title: string;
  fileName: string;
  // 出力条件に関する情報を表示
  conditionInfos: conditionInfoType[];
  status: reportStatusType;
  startDate?: Date;
  endDate?: Date;
  blobUrl?: string;
  blob?: Blob;
};

// 帳票ごとの個別定義
type legalCurriculumReportType = {
  reportType: "LEGAL_CURRICULUM";
  reportDataFunc: () => Promise<legalCurriculumPdfParamType>;
};
type legalCurriculumPdfInfo = pdfInfoBase & legalCurriculumReportType;

// Excel用の型定義
type legalCurriculumExcelReportType = {
  reportType: "LEGAL_CURRICULUM_EXCEL";
  reportDataFunc: () => Promise<LegalCurriculumExcelParams>;
};
type legalCurriculumExcelInfo = pdfInfoBase & legalCurriculumExcelReportType;

// サーバーサイド法定研修実績レポート用の型定義
type serverLegalCurriculumResultReportType = {
  reportType: "SERVER_LEGAL_CURRICULUM_RESULT";
  exportId: number;
  serverStatus: legalCurriculumResultExportStatusType;
  dataLoaded: boolean;
  errorMessage?: string;
};
type serverLegalCurriculumResultPdfInfo = pdfInfoBase & serverLegalCurriculumResultReportType;

type serverAccountPortalLegalUserListReportType = {
  reportType: "SERVER_ACCOUNT_PORTAL_LEGAL_USER_LIST";
  exportId: number;
  serverStatus: legalCurriculumResultExportStatusType;
  dataLoaded: boolean;
  errorMessage?: string;
};
type serverAccountPortalLegalUserListPdfInfo = pdfInfoBase & serverAccountPortalLegalUserListReportType;

export type reportLogic = legalCurriculumReportType | legalCurriculumExcelReportType | serverLegalCurriculumResultReportType | serverAccountPortalLegalUserListReportType;
export type reportDataType =
  | { reportType: "LEGAL_CURRICULUM"; reportData: legalCurriculumPdfParamType }
  | { reportType: "LEGAL_CURRICULUM_EXCEL"; reportData: LegalCurriculumExcelParams }
  | { reportType: "SERVER_LEGAL_CURRICULUM_RESULT"; exportId: number }
  | { reportType: "SERVER_ACCOUNT_PORTAL_LEGAL_USER_LIST"; exportId: number };

export type pdfInfoType = legalCurriculumPdfInfo | legalCurriculumExcelInfo | serverLegalCurriculumResultPdfInfo | serverAccountPortalLegalUserListPdfInfo;

export type PdfInfoList = pdfInfoType[];

const initialState = [] as PdfInfoList;

const PdfContext = createContext(
  {} as {
    pdfContext: PdfInfoList;
  }
);

export type PdfDispatch = React.Dispatch<action>;
type removePdfAction = {
  type: "REMOVE_PDF";
  id: string;
};
type clearAllPdfAction = {
  type: "CLEAR_ALL_PDF";
};

type setErrorAction = {
  type: "SET_ERROR";
  id: string;
  status: "ERROR";
  endDate: Date;
};

type setSuccessAction = {
  type: "SET_SUCCESS";
  id: string;
  status: "SUCCESS";
  endDate: Date;
  blobUrl: string;
  blob: Blob;
};
type addLegalCurriculumPdfAction = {
  type: "ADD_PDF";
  id: string;
  // 出力帳票名
  title: string;
  fileName: string;
  // 出力条件に関する情報を表示
  conditionInfos: conditionInfoType[];
  // 帳票のデータ習得
  reportDataFunc: () => Promise<legalCurriculumPdfParamType>;
  // 帳票の種類
  reportType: "LEGAL_CURRICULUM";
  status: "PROCESSING";
  startDate: Date;
};

type addLegalCurriculumExcelAction = {
  type: "ADD_PDF";
  id: string;
  // 出力帳票名
  title: string;
  fileName: string;
  // 出力条件に関する情報を表示
  conditionInfos: conditionInfoType[];
  // 帳票のデータ
  reportDataFunc: () => Promise<LegalCurriculumExcelParams>;
  // 帳票の種類
  reportType: "LEGAL_CURRICULUM_EXCEL";
  status: "PROCESSING";
  startDate: Date;
};

type addServerLegalCurriculumResultPdfAction = {
  type: "ADD_SERVER_PDF";
  id: string;
  title: string;
  fileName: string;
  conditionInfos: conditionInfoType[];
  exportId: number;
  reportType: "SERVER_LEGAL_CURRICULUM_RESULT" | "SERVER_ACCOUNT_PORTAL_LEGAL_USER_LIST";
  status: "PROCESSING";
  serverStatus: legalCurriculumResultExportStatusType;
  dataLoaded: boolean;
  startDate: Date;
};

type updateServerPdfStatusAction = {
  type: "UPDATE_SERVER_PDF_STATUS";
  id: string;
  serverStatus: legalCurriculumResultExportStatusType;
  dataLoaded: boolean;
  errorMessage?: string;
  blobUrl?: string;
  fileName?: string;
};

type action =
  | removePdfAction
  | clearAllPdfAction
  | setErrorAction
  | setSuccessAction
  | addLegalCurriculumPdfAction
  | addLegalCurriculumExcelAction
  | addServerLegalCurriculumResultPdfAction
  | updateServerPdfStatusAction;

const PdfDispatchContext = createContext(
  {} as {
    dispatch: PdfDispatch;
  }
);

export const addLegalCurriculumPdf = (
  title: string,
  fileName: string,
  conditionInfos: conditionInfoType[],
  reportDataFunc: () => Promise<legalCurriculumPdfParamType>
): action => {
  return {
    type: "ADD_PDF",
    id: uuidv4(),
    title,
    fileName,
    conditionInfos,
    reportType: "LEGAL_CURRICULUM",
    reportDataFunc,
    status: "PROCESSING",
    startDate: new Date(),
  };
};

export const addLegalCurriculumExcel = (
  title: string,
  fileName: string,
  conditionInfos: conditionInfoType[],
  reportDataFunc: () => Promise<LegalCurriculumExcelParams>
): action => {
  return {
    type: "ADD_PDF",
    id: uuidv4(),
    title,
    fileName,
    conditionInfos,
    reportType: "LEGAL_CURRICULUM_EXCEL",
    reportDataFunc,
    status: "PROCESSING",
    startDate: new Date(),
  };
};

export const addServerLegalCurriculumResultPdf = (
  title: string,
  fileName: string,
  conditionInfos: conditionInfoType[],
  exportId: number
): action => {
  return {
    type: "ADD_SERVER_PDF",
    id: String(exportId), // 数値IDを文字列に変換してidとして使用
    title,
    fileName,
    conditionInfos,
    exportId,
    reportType: "SERVER_LEGAL_CURRICULUM_RESULT",
    status: "PROCESSING",
    serverStatus: "WAITING",
    dataLoaded: false,
    startDate: new Date(),
  };
};

export const addServerAccountPortalLegalUserListPdf = (
  title: string,
  fileName: string,
  conditionInfos: conditionInfoType[],
  exportId: number
): action => {
  return {
    type: "ADD_SERVER_PDF",
    id: `aplul_${exportId}`,
    title,
    fileName,
    conditionInfos,
    exportId,
    reportType: "SERVER_ACCOUNT_PORTAL_LEGAL_USER_LIST",
    status: "PROCESSING",
    serverStatus: "WAITING",
    dataLoaded: false,
    startDate: new Date(),
  };
};

export const updateServerPdfStatus = (
  id: string,
  serverStatus: legalCurriculumResultExportStatusType,
  dataLoaded: boolean,
  errorMessage?: string,
  blobUrl?: string,
  fileName?: string
): action => {
  return {
    type: "UPDATE_SERVER_PDF_STATUS",
    id,
    serverStatus,
    dataLoaded,
    errorMessage,
    blobUrl,
    fileName,
  };
};

export const removePdf = (id: string): action => {
  return {
    type: "REMOVE_PDF",
    id,
  };
};

export const clearAllPdf = (): action => {
  return {
    type: "CLEAR_ALL_PDF",
  };
};

export const setError = (id: string): action => {
  return {
    type: "SET_ERROR",
    id,
    status: "ERROR",
    endDate: new Date(),
  };
};

export const setSuccess = (id: string, blobUrl: string, blob: Blob): action => {
  return {
    type: "SET_SUCCESS",
    id,
    status: "SUCCESS",
    endDate: new Date(),
    blobUrl,
    blob,
  };
};

const pdfReducer = (state: PdfInfoList, action: action): PdfInfoList => {
  switch (action.type) {
    case "ADD_PDF":
      return [...state, action as pdfInfoType];
    case "ADD_SERVER_PDF":
      return [...state, action as unknown as pdfInfoType];
    case "REMOVE_PDF":
      return state.filter((item) => item.id != action.id);
    case "CLEAR_ALL_PDF":
      return [];
    case "SET_ERROR":
      return state.map((item) => {
        if (item.id == action.id) {
          return {
            ...item,
            status: action.status || "ERROR",
            endDate: action.endDate,
          };
        }
        return item;
      });
    case "SET_SUCCESS":
      return state.map((item) => {
        if (item.id == action.id) {
          return {
            ...item,
            status: action.status || "ERROR",
            endDate: action.endDate,
            blobUrl: action.blobUrl,
          };
        }
        return item;
      });
    case "UPDATE_SERVER_PDF_STATUS":
      return state.map((item) => {
        if (item.id == action.id && (item.reportType === "SERVER_LEGAL_CURRICULUM_RESULT" || item.reportType === "SERVER_ACCOUNT_PORTAL_LEGAL_USER_LIST")) {
          const newStatus: reportStatusType =
            action.serverStatus === "SUCCESS" ? "SUCCESS" :
            action.serverStatus === "FAILED" ? "ERROR" : "PROCESSING";
          return {
            ...item,
            status: newStatus,
            serverStatus: action.serverStatus,
            dataLoaded: action.dataLoaded,
            errorMessage: action.errorMessage,
            ...(action.blobUrl && { blobUrl: action.blobUrl }),
            ...(action.fileName && { fileName: action.fileName }),
            endDate: action.serverStatus === "SUCCESS" || action.serverStatus === "FAILED" ? new Date() : undefined,
          };
        }
        return item;
      });
    default:
      return state;
  }
};

export const PdfProvider = (props: childrenProps) => {
  const [contextData, dispatch] = useReducer(pdfReducer, initialState);
  const pdfContext = useMemo(
    () => ({
      pdfContext: contextData,
    }),
    [contextData]
  );
  const pdfDispatch = useMemo(
    () => ({
      dispatch,
    }),
    []
  );

  return (
    <PdfContext.Provider value={pdfContext}>
      <PdfDispatchContext.Provider value={pdfDispatch} {...props} />
    </PdfContext.Provider>
  );
};

export const usePdfContext = () => {
  return useContext(PdfContext);
};

export const usePdfDispatch = () => {
  return useContext(PdfDispatchContext);
};
